import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Link2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";

const labelOf = (w) => [w.producer_name, w.area_name, w.surface_location, w.downhole_location].filter(Boolean).join(" · ");

export function WellPicker({ value, onPick, onClear, title = "Pumpjack records" }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setRes([]); return; }
    const t = setTimeout(() => api.get("/admin/pumpjack/search", { params: { q } }).then((r) => setRes(r.data)).catch((e) => toast.error(errMsg(e))), 300);
    return () => clearTimeout(t);
  }, [q]);
  const pick = (w) => { onPick({ surface: w.surface_location, downhole: w.downhole_location || "", label: labelOf(w) }); setQ(""); };
  return (
    <div className="rounded-xl border p-3 space-y-2" data-testid="pumpjack-link">
      <Label>{title}</Label>
      <p className="text-[11px] text-slate-500">Search by LSD (surface or downhole location), producer or area. Records show to signed-in staff and to anyone who unlocks a password-protected tour.</p>
      {value?.surface && <div className="flex items-center gap-2 text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid="pumpjack-linked"><Link2 className="w-4 h-4 shrink-0" /><span className="flex-1 min-w-0 truncate">{value.label || value.surface}</span><button type="button" onClick={onClear} className="p-1 text-slate-500 hover:text-red-600" title="Unlink" data-testid="pumpjack-unlink"><X className="w-4 h-4" /></button></div>}
      <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" /><Input className="pl-9" placeholder={value?.surface ? "Search to change the well…" : "e.g. 1-4-18-14W4"} value={q} onChange={(e) => setQ(e.target.value)} data-testid="pumpjack-search" /></div>
      {res.length > 0 && (
        <div className="max-h-48 overflow-y-auto border rounded-lg divide-y" data-testid="pumpjack-results">
          {res.map((w, i) => (
            <button type="button" key={i} onClick={() => pick(w)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50" data-testid={`pumpjack-result-${i}`}>
              <span className="font-semibold">{w.surface_location}</span>{w.downhole_location && <span> → {w.downhole_location}</span>}
              <span className="block text-slate-500">{[w.producer_name, w.area_name, w.pump_name, `${w.wr_count || 0} WRs`].filter(Boolean).join(" · ")}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function PumpjackLink({ hotspotId }) {
  const [link, setLink] = useState(null);
  useEffect(() => {
    if (hotspotId) { api.get(`/admin/hotspots/${hotspotId}/pumpjack`).then((r) => setLink(r.data.surface ? r.data : null)); }
  }, [hotspotId]);
  if (!hotspotId) return <p className="rounded-xl border p-3 text-xs text-slate-500">Save this hotspot first, then open it again to link a different well to it.</p>;
  const pick = async (body) => {
    try { const { data } = await api.put(`/admin/hotspots/${hotspotId}/pumpjack`, body); setLink(data); toast.success("Pumpjack records linked"); } catch (e) { toast.error(errMsg(e)); }
  };
  const clear = async () => { await api.delete(`/admin/hotspots/${hotspotId}/pumpjack`); setLink(null); toast.success("Unlinked"); };
  return <WellPicker value={link} onPick={pick} onClear={clear} title="Well for this hotspot (optional; leave empty to use the tour's well)" />;
}
